import { useNavigate } from "react-router-dom"
import { type Toast, useToast } from "../../stores/toast"
import { BellIcon, InfoIcon, CheckCircleIcon, WarningIcon, XCircleIcon } from "../icons"

const variantStyles = {
  info: {
    bg: "bg-blue-600",
    icon: BellIcon,
  },
  success: {
    bg: "bg-emerald-600",
    icon: CheckCircleIcon,
  },
  warning: {
    bg: "bg-amber-500",
    icon: WarningIcon,
  },
  danger: {
    bg: "bg-red-600",
    icon: XCircleIcon,
  },
}

function ToastItem({ toast }: { toast: Toast }) {
  const { dismissToast } = useToast()
  const navigate = useNavigate()
  const style = variantStyles[toast.variant]
  const IconComp = style.icon

  return (
    <div className="animate-toast-in pointer-events-auto w-96 overflow-hidden rounded-lg bg-white shadow-2xl ring-1 ring-black/5">
      <div className="flex">
        <div className={`flex w-12 shrink-0 items-center justify-center ${style.bg}`}>
          <IconComp className="h-5 w-5 text-white" />
        </div>
        <div className="flex flex-1 items-start gap-3 p-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">{toast.title}</p>
            <p className="mt-0.5 text-sm text-slate-600 line-clamp-2">{toast.body}</p>
            {toast.action ? (
              <button
                type="button"
                className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800"
                onClick={() => {
                  navigate(toast.action!.path)
                  dismissToast(toast.id)
                }}
              >
                {toast.action.label}
              </button>
            ) : null}
          </div>
          <button
            type="button"
            className="shrink-0 rounded p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Dismiss"
            onClick={() => dismissToast(toast.id)}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      {/* Auto-dismiss progress bar */}
      <div className="h-1 w-full bg-slate-100">
        <div className={`h-full ${style.bg} opacity-40 animate-toast-progress`} />
      </div>
    </div>
  )
}

export function ToastContainer() {
  const { toasts } = useToast()

  if (toasts.length === 0) return null

  return (
    <div
      aria-live="polite"
      className="fixed top-4 right-4 z-[100] flex flex-col gap-3 pointer-events-none"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  )
}
